import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { Drawer } from '../ui/Drawer';
import { VpSelect } from '../ui/VpSelect';
import { useCreateWarehouse, useUpdateWarehouse } from '../../api/platformTenants';
import { IANA_TIMEZONES, DEFAULT_TIMEZONE } from '../../constants/ianaTimezones';

type CreateProps = {
  mode: 'create'; tenantId: number;
  isOpen: boolean; onClose: () => void;
};
type EditProps = {
  mode: 'edit'; tenantId: number;
  initial: { id: number; code: string; name: string; timezone: string; address: string | null };
  isOpen: boolean; onClose: () => void;
};
type Props = CreateProps | EditProps;

export function WarehouseDrawer(props: Props) {
  const isEdit = props.mode === 'edit';
  const initial = isEdit ? props.initial : { code: '', name: '', timezone: DEFAULT_TIMEZONE, address: null };

  const [code, setCode] = useState(initial.code);
  const [name, setName] = useState(initial.name);
  const [timezone, setTimezone] = useState(initial.timezone);
  const [address, setAddress] = useState(initial.address ?? '');
  const [error, setError] = useState<string | null>(null);

  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const createMut = useCreateWarehouse(props.tenantId, token);
  const updateMut = useUpdateWarehouse(token);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEdit) {
        await updateMut.mutateAsync({ id: (props as EditProps).initial.id,
          patch: { name, timezone, address: address || null } });
      } else {
        await createMut.mutateAsync({
          body: { code, name, timezone, address: address || null },
          idempotencyKey: crypto.randomUUID(),
        });
      }
      props.onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Errore sconosciuto');
    }
  };

  return (
    <Drawer
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={isEdit ? `Modifica warehouse ${initial.code}` : 'Nuovo warehouse'}
    >
      <form onSubmit={onSubmit} className="d-flex flex-column gap-2">
        <FormGroup>
          <Label for="wd-code" className="mb-1">Code</Label>
          <Input
            id="wd-code"
            aria-label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={isEdit}
            pattern="^[A-Z][A-Z0-9-]+$"
            maxLength={32}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label for="wd-name" className="mb-1">Nome</Label>
          <Input
            id="wd-name"
            aria-label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={128}
            required
          />
        </FormGroup>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="form-label mb-0">Timezone</span>
          <VpSelect
            ariaLabel="Timezone"
            options={IANA_TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
            value={timezone}
            onChange={(v) => setTimezone(String(v ?? ''))}
          />
        </label>
        <FormGroup className="mt-1">
          <Label for="wd-address" className="mb-1">Indirizzo (opzionale)</Label>
          <Input id="wd-address" type="textarea" value={address} onChange={(e) => setAddress(e.target.value)} maxLength={500} rows={2} />
        </FormGroup>

        {error && <p className="text-danger small mb-0">{error}</p>}

        <div className="d-flex justify-content-end gap-2">
          <Button type="button" color="light" size="sm" onClick={props.onClose}>Annulla</Button>
          <Button type="submit" color="primary" size="sm"
                  disabled={createMut.isPending || updateMut.isPending}>
            {isEdit ? 'Salva modifiche' : 'Crea warehouse'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
